/**
 * Sentry Configuration
 * Error tracking and performance monitoring
 */

import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for browser
 * Only in production to avoid noise during development
 */
export function initSentry() {
    // Only initialize in production
    if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
        Sentry.init({
            dsn: import.meta.env.VITE_SENTRY_DSN,

            // Performance Monitoring
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration({
                    maskAllText: false,
                    blockAllMedia: false,
                }),
            ],

            // Performance traces sample rate (100% in production for now)
            tracesSampleRate: 1.0,

            // Session Replay (10% of sessions, 100% on error)
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,

            // Environment
            environment: import.meta.env.MODE,

            // Release tracking
            release: import.meta.env.VITE_APP_VERSION || '1.0.0',

            // Ignore common non-critical errors
            ignoreErrors: [
                // Browser extensions
                'top.GLOBALS',
                'chrome-extension://',
                'moz-extension://',
                // Network errors (handled by app)
                'NetworkError',
                'Failed to fetch',
                // Cancelled requests
                'AbortError',
                'cancelled',
            ],

            // Before send hook - filter sensitive data
            beforeSend(event, hint) {
                // Remove sensitive data from breadcrumbs
                if (event.breadcrumbs) {
                    event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
                        if (breadcrumb.data) {
                            // Remove password fields
                            if (breadcrumb.data.password) {
                                breadcrumb.data.password = '[Filtered]';
                            }
                            if (breadcrumb.data.token) {
                                breadcrumb.data.token = '[Filtered]';
                            }
                        }
                        return breadcrumb;
                    });
                }

                return event;
            },
        });

    } else if (import.meta.env.DEV) {
    } else {
        console.warn('⚠️ Sentry DSN not configured');
    }
}

/**
 * Capture exception manually
 */
export function captureException(error, context = {}) {
    if (import.meta.env.PROD) {
        Sentry.captureException(error, {
            extra: context
        });
    } else {
        console.error('Error:', error, context);
    }
}

/**
 * Capture message manually
 */
export function captureMessage(message, level = 'info') {
    if (import.meta.env.PROD) {
        Sentry.captureMessage(message, level);
    } else {
    }
}

/**
 * Set user context
 */
export function setUser(user) {
    if (import.meta.env.PROD && user) {
        Sentry.setUser({
            id: user.id,
            email: user.email,
            username: user.name
        });
    }
}

/**
 * Clear user context (on logout)
 */
export function clearUser() {
    if (import.meta.env.PROD) {
        Sentry.setUser(null);
    }
}

export default {
    init: initSentry,
    captureException,
    captureMessage,
    setUser,
    clearUser
};
